import { useState } from 'react';
import { Plus, Tag, Trash2, Edit3, Check, X } from 'lucide-react';
import { BRAND } from '@/constants/brand';
import { useAuth } from '@/context/AuthContext';
import { useData } from '@/context/DataContext';
import Field from '@/components/ui/Field';
import CheckOption from '@/components/ui/CheckOption';
import { useConfirm } from '@/components/ui/useConfirm';

export default function DiscountAdmin() {
  const { user } = useAuth();
  const { discountCodes, updateDiscountCodes } = useData();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    descuento: 10,
    activo: true,
    descripcion: '',
  });
  const [confirm, ConfirmUI] = useConfirm();

  const resetForm = () => {
    setForm({ codigo: '', nombre: '', descuento: 10, activo: true, descripcion: '' });
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!form.codigo || !form.nombre) return;
    const newCode = editing
      ? { ...editing, ...form, codigo: form.codigo.toUpperCase() }
      : {
          id: `code-${Date.now()}`,
          codigo: form.codigo.toUpperCase(),
          nombre: form.nombre,
          descuento: parseFloat(form.descuento),
          activo: form.activo,
          descripcion: form.descripcion,
          creadoPor: user.nombre,
          fechaCreacion: new Date().toISOString(),
        };
    const newCodes = editing
      ? discountCodes.map((c) => (c.id === editing.id ? newCode : c))
      : [...discountCodes, newCode];
    await updateDiscountCodes(newCodes);
    setShowForm(false);
    resetForm();
  };

  const toggleActivo = async (id) => {
    const newCodes = discountCodes.map((c) =>
      c.id === id ? { ...c, activo: !c.activo } : c
    );
    await updateDiscountCodes(newCodes);
  };

  const eliminar = async (id) => {
    const ok = await confirm('¿Eliminar este código?');
    if (!ok) return;
    await updateDiscountCodes(discountCodes.filter((c) => c.id !== id));
  };

  const editar = (c) => {
    setEditing(c);
    setForm({
      codigo: c.codigo,
      nombre: c.nombre,
      descuento: c.descuento,
      activo: c.activo,
      descripcion: c.descripcion || '',
    });
    setShowForm(true);
  };

  return (
    <div className="flex-1 overflow-auto bg-neutral-950">
      {ConfirmUI}
      <div className="max-w-5xl mx-auto px-8 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Códigos de descuento</h1>
            <p className="text-neutral-500 text-sm">
              Crea códigos promocionales activables por tu equipo comercial
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium hover:scale-[1.02] transition-transform"
            style={{ background: BRAND.gold, color: BRAND.black }}
          >
            <Plus className="w-4 h-4" /> Nuevo código
          </button>
        </div>

        {discountCodes.length === 0 ? (
          <div className="text-center py-20 bg-neutral-900/30 rounded-2xl border border-dashed border-neutral-800">
            <Tag className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-400 mb-2">No hay códigos de descuento todavía</p>
            <button
              onClick={() => setShowForm(true)}
              className="text-sm font-medium"
              style={{ color: BRAND.gold }}
            >
              Crear el primero →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {discountCodes.map((c) => (
              <div
                key={c.id}
                className={`p-5 rounded-xl border ${
                  c.activo
                    ? 'border-brand-gold/30 bg-brand-gold/5'
                    : 'border-neutral-800 bg-neutral-900/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        c.activo
                          ? 'bg-brand-gold text-black'
                          : 'bg-neutral-800 text-neutral-500'
                      }`}
                    >
                      <Tag className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-white">{c.codigo}</span>
                        <span className="text-brand-gold font-bold">{c.descuento}%</span>
                        {c.activo ? (
                          <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 rounded uppercase tracking-wider">
                            Activo
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 bg-neutral-700 text-neutral-400 rounded uppercase tracking-wider">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-400 mt-1">{c.nombre}</div>
                      {c.descripcion && (
                        <div className="text-xs text-neutral-500 mt-1">{c.descripcion}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleActivo(c.id)}
                      className="p-2 text-neutral-500 hover:text-white hover:bg-neutral-800 rounded"
                      title={c.activo ? 'Desactivar' : 'Activar'}
                    >
                      {c.activo ? (
                        <X className="w-4 h-4" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => editar(c)}
                      className="p-2 text-neutral-500 hover:text-white hover:bg-neutral-800 rounded"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => eliminar(c.id)}
                      className="p-2 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50 p-4">
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-white mb-5">
                {editing ? 'Editar código' : 'Nuevo código'}
              </h3>
              <div className="space-y-4">
                <Field
                  label="Código *"
                  value={form.codigo}
                  onChange={(v) => setForm({ ...form, codigo: v.toUpperCase() })}
                  placeholder="PROPTECH2026"
                />
                <Field
                  label="Nombre *"
                  value={form.nombre}
                  onChange={(v) => setForm({ ...form, nombre: v })}
                  placeholder="PropTech LATAM 2026"
                />
                <div>
                  <label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">
                    Descuento %
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="25"
                    value={form.descuento}
                    onChange={(e) =>
                      setForm({ ...form, descuento: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full px-4 py-2.5 bg-black/40 border border-neutral-800 rounded-lg text-white focus:outline-none focus:border-brand-gold/50 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">
                    Descripción (opcional)
                  </label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 bg-black/40 border border-neutral-800 rounded-lg text-white focus:outline-none focus:border-brand-gold/50 text-sm resize-none"
                  />
                </div>
                <CheckOption
                  checked={form.activo}
                  onChange={(v) => setForm({ ...form, activo: v })}
                  label="Código activo"
                />
              </div>
              <div className="flex items-center justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm text-neutral-400 hover:text-white"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!form.codigo || !form.nombre}
                  className="px-5 py-2.5 rounded-lg font-medium disabled:opacity-30"
                  style={{ background: BRAND.gold, color: BRAND.black }}
                >
                  {editing ? 'Guardar cambios' : 'Crear código'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
